import CreateEditTaskPopUp from "../CreateEditTask/CreateEditTaskPopUp";
import React, { useEffect, useState } from "react";
import { Button, CircularProgress } from "@mui/material";
import PlaylistAddCheckOutlinedIcon from "@mui/icons-material/PlaylistAddCheckOutlined";
import { useDispatch, useSelector } from "react-redux";
import { isEmpty } from "lodash";
import {
  markJobAsCompleted,
  markJobAsCompletedSuccess,
  convertJobToInvoice,
  convertJobToInvoiceSuccess,
  updateJob as onUpdateJob,
} from "../../../store/jobsList/actions";
import { timeFormatDateAndTime } from "../../Common/HandleDateTime";
import { toast } from "react-toastify";
import {
  getSingleJobDetailsForTimer,
  readSingleJob,
} from "../../../store/singleJob/action";
import AppModal from "../../Common/AppModal";
import InvoiceItems from "../../../pages/Jobs/singleJob/InvoiceItems";
import JobTimeEntriesByProduct from "../../Common/JobTimeEntriesByProduct";

export default function StopTimerChooseNext({
  modal,
  toggle,

  Customer_Id,
  Staff_Id,
  Job_Id,
  // New self-invoicing flow (gated per staff by ZCONFIGVAL_52_X). When false
  // this modal behaves exactly as before (Add New Task / Mark Job as completed).
  invoiceReviewEnabled = false,
  // True once the parent has committed the billable timer, so the priced
  // JobInvoiceItem line has been generated and is safe to load/edit.
  invoiceReviewReady = false,
  // Parent-owned handler: stop/commit the billable timer (generating the line)
  // and auto-start the non-billable invoicing/review timer.
  onStartInvoiceReview,
}) {
  const [modalTask, setModalTask] = useState(false);
  const toggleTask = () => setModalTask(!modalTask);

  // choose = pick Add Task / Complete; review = edit billable lines then complete/invoice
  const [step, setStep] = useState("choose");
  // Track which final action is in flight so the success effects can chain.
  const [invoiceNowPending, setInvoiceNowPending] = useState(false);
  const [completing, setCompleting] = useState(false);

  const dispatch = useDispatch();

  // Simple (existing) flow: mark complete straight away.
  const handleJobStatusUpdate = () => {
    dispatch(markJobAsCompleted([Job_Id]));
  };

  // New flow: commit the timer (parent) and move into the line-item review step.
  const handleStartReview = () => {
    if (typeof onStartInvoiceReview === "function") {
      onStartInvoiceReview();
    }
    setStep("review");
  };

  // Stamp the job as reviewed now. Only the review-step completes call this -
  // the quick complete leaves the job un-reviewed. The backend clears this
  // datetime again whenever more time is later allocated to the job.
  const markReviewed = () => {
    dispatch(
      onUpdateJob({
        Id: Job_Id,
        InvoiceReviewedDateTime: timeFormatDateAndTime(new Date()),
      })
    );
  };

  // Quick complete from the choose step (no review) - does NOT mark reviewed.
  const handleQuickComplete = () => {
    setCompleting(true);
    dispatch(markJobAsCompleted([Job_Id]));
  };

  const handleCompleteInvoiceLater = () => {
    setCompleting(true);
    markReviewed();
    dispatch(markJobAsCompleted([Job_Id]));
  };

  const handleCompleteAndInvoiceNow = () => {
    setInvoiceNowPending(true);
    markReviewed();
    dispatch(markJobAsCompleted([Job_Id]));
  };

  const { jobUpdateSuccessAPI, markJobAsCompletedSuccessLoading } = useSelector(
    (state) => ({
      jobUpdateSuccessAPI: state.Jobs.markJobAsCompletedSuccess,
      markJobAsCompletedSuccessLoading:
        state.Jobs.markJobAsCompletedSuccessLoading,
    })
  );

  const { convertSuccessAPI, convertLoading } = useSelector((state) => ({
    convertSuccessAPI: state.Jobs.jobConversionToInvoiceSuccess,
    convertLoading: state.Jobs.convertJobToInvoiceLoading,
  }));

  // Used only to disable "Invoice now" when the job has no billable lines
  // (raising an empty invoice makes no sense). The list itself is rendered by
  // the embedded InvoiceItems component.
  const { billableItems } = useSelector((state) => ({
    billableItems: state.SingleJob.billableJobItemsList?.jobinvoiceitem_list,
  }));
  const hasBillableLines = !isEmpty(billableItems);

  // Mark-complete result. In the simple flow this just refreshes (as before).
  // In the new flow it either finishes (invoice later) or chains into
  // convert-to-invoice (invoice now).
  useEffect(() => {
    if (isEmpty(jobUpdateSuccessAPI)) return;
    if (jobUpdateSuccessAPI.status === 200) {
      dispatch(getSingleJobDetailsForTimer({ id: Job_Id }));
      dispatch(readSingleJob({ id: Job_Id }));
      if (invoiceNowPending) {
        dispatch(convertJobToInvoice([Job_Id]));
      } else if (completing) {
        toast.success(jobUpdateSuccessAPI.message || "Job completed");
        setCompleting(false);
        toggle();
      }
    } else {
      toast.error(jobUpdateSuccessAPI.message);
      setInvoiceNowPending(false);
      setCompleting(false);
    }
    dispatch(markJobAsCompletedSuccess([]));
  }, [jobUpdateSuccessAPI]);

  // Convert-to-invoice result (invoice-now path only).
  useEffect(() => {
    if (isEmpty(convertSuccessAPI)) return;
    if (convertSuccessAPI.status === 200) {
      toast.success(convertSuccessAPI.message || "Invoice created");
    } else {
      toast.error(convertSuccessAPI.message);
    }
    setInvoiceNowPending(false);
    dispatch(convertJobToInvoiceSuccess([]));
    toggle();
  }, [convertSuccessAPI]);

  const busy =
    markJobAsCompletedSuccessLoading || convertLoading || invoiceNowPending;

  // ----- Footers -------------------------------------------------------------
  // "Choose" step. When the invoice-review flow is enabled the staff member gets
  // BOTH a plain "Mark Job as completed" and a separate "Mark Complete & Review
  // Invoice"; otherwise just the original plain complete.
  const chooseFooter = (
    <>
      <Button variant="outlined" color="info" onClick={() => toggleTask()}>
        Add New Task
      </Button>
      {invoiceReviewEnabled ? (
        <>
          <Button
            variant="outlined"
            color="success"
            disabled={busy}
            onClick={() => handleQuickComplete()}
          >
            Mark Job as completed
          </Button>
          <Button
            variant="contained"
            color="success"
            disabled={busy}
            onClick={() => handleStartReview()}
          >
            Mark Complete &amp; Review Invoice
          </Button>
        </>
      ) : (
        <Button
          variant="contained"
          color="success"
          disabled={markJobAsCompletedSuccessLoading}
          onClick={() => handleJobStatusUpdate()}
        >
          Mark Job as completed
        </Button>
      )}
    </>
  );

  const reviewFooter = (
    <>
      <Button
        variant="outlined"
        color="success"
        disabled={busy || !invoiceReviewReady}
        onClick={() => handleCompleteInvoiceLater()}
      >
        Complete (invoice later)
      </Button>
      <Button
        variant="contained"
        color="success"
        disabled={busy || !invoiceReviewReady || !hasBillableLines}
        onClick={() => handleCompleteAndInvoiceNow()}
      >
        Complete &amp; Invoice now
      </Button>
    </>
  );

  const isReviewStep = invoiceReviewEnabled && step === "review";

  return (
    <>
      <CreateEditTaskPopUp
        modalTask={modalTask}
        setModalTask={setModalTask}
        toggleTask={toggleTask}
        Job_Id={Job_Id}
        Customer_Id={Customer_Id}
        Staff_Id={Staff_Id}
        isCreate={true}
        isEdit={false}
        standAlone={true}
        ShowJobList={true}
        stopStartTimer={true}
      />

      <AppModal
        open={modal}
        onClose={toggle}
        title={isReviewStep ? "Review Invoice Items" : "Choose Whats Next"}
        icon={<PlaylistAddCheckOutlinedIcon />}
        headerAccent="indigo"
        size={isReviewStep ? "lg" : "sm"}
        loading={busy}
        footer={isReviewStep ? reviewFooter : chooseFooter}
      >
        {isReviewStep ? (
          invoiceReviewReady ? (
            <>
              <p className="text-muted">
                Review and edit the billable items generated from your tracked
                time, then complete the job. Choose <strong>invoice later</strong>{" "}
                to leave it in unbilled, or <strong>Invoice now</strong> to raise
                the invoice immediately.
              </p>
              <InvoiceItems jobId={Job_Id} Customer_Id={Customer_Id} />
              <h5 className="mt-4 mb-2">Time Entries by Product</h5>
              <p className="text-muted mb-2">
                Tracked time on this job, grouped by product and split by person.
              </p>
              <JobTimeEntriesByProduct jobId={Job_Id} />
            </>
          ) : (
            <div className="d-flex align-items-center justify-content-center flex-column py-5 gap-3">
              <CircularProgress size={28} />
              <p className="mb-0 text-muted">Preparing invoice items…</p>
            </div>
          )
        ) : (
          <p className="mb-0 text-muted">
            Add another task or mark this job as completed.
          </p>
        )}
      </AppModal>
    </>
  );
}
